import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  IconButton,
  Tooltip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Divider,
} from '@mui/material';
import {
  Image as ImageIcon,
  InsertDriveFile as FileIcon,
  Download as DownloadIcon,
  FolderOpen as EmptyIcon,
  Refresh as RefreshIcon,
  CloudQueue as CloudIcon,
} from '@mui/icons-material';
import apiClient from '../../../utils/api';
import { FileViewer } from '../../FileViewer';

interface FileRecord {
  message_id: string;
  room_id: string;
  room_title: string;
  sender_name: string;
  sender_type: string;
  file_url: string;
  file_name: string;
  file_size: number | null;
  file_type: string | null;
  created_at: string;
  storage_type: string;
}

interface RoomFilesProps {
  selectedChatId: string | null;
}

const formatFileSize = (bytes: number | null): string => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDate = (dateStr: string): string => {
  return new Date(dateStr).toLocaleString('ja-JP', {
    month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
  });
};

const isImageFile = (fileName: string): boolean => {
  return /\.(png|jpe?g|gif|webp)$/i.test(fileName);
};

const getDownloadUrl = (file: FileRecord): string => {
  const token = localStorage.getItem('adminToken') || '';
  if (file.file_url.startsWith('s3:')) {
    return `/api/files/${file.message_id}/download?token=${encodeURIComponent(token)}`;
  }
  return `${file.file_url}?token=${token}`;
};

export const RoomFiles: React.FC<RoomFilesProps> = ({ selectedChatId }) => {
  const [files, setFiles] = useState<FileRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [viewerMessageId, setViewerMessageId] = useState<string | null>(null);
  const [viewerFileName, setViewerFileName] = useState<string | undefined>();

  const fetchFiles = useCallback(async () => {
    if (!selectedChatId) {
      setFiles([]);
      setTotal(0);
      return;
    }
    setLoading(true);
    try {
      const response = await apiClient.getFiles({
        roomId: selectedChatId,
        limit: 100,
      });
      if (response.success && response.data) {
        setFiles(response.data.files);
        setTotal(response.data.pagination.total);
      }
    } catch (err) {
      console.error('ルームファイル取得エラー:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedChatId]);

  useEffect(() => {
    fetchFiles();
  }, [fetchFiles]);

  if (!selectedChatId) {
    return (
      <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
        <Box sx={{ textAlign: 'center', color: 'text.secondary' }}>
          <EmptyIcon sx={{ fontSize: 48, mb: 1 }} />
          <Typography variant="body2">
            チャットを選択すると<br />ファイル一覧が表示されます
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          添付ファイル
          {total > 0 && (
            <Chip label={total} size="small" sx={{ ml: 1, height: 20, fontSize: '0.7rem' }} />
          )}
        </Typography>
        <Tooltip title="更新">
          <IconButton size="small" onClick={fetchFiles} disabled={loading}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : files.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
            <EmptyIcon sx={{ fontSize: 36, mb: 1, opacity: 0.5 }} />
            <Typography variant="body2">ファイルはありません</Typography>
          </Box>
        ) : (
          <List dense disablePadding>
            {files.map((file, index) => (
              <React.Fragment key={file.message_id}>
                {index > 0 && <Divider component="li" />}
                <ListItem
                  sx={{ px: 2, py: 1, alignItems: 'flex-start', cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                  onClick={() => { setViewerMessageId(file.message_id); setViewerFileName(file.file_name); }}
                  secondaryAction={
                    <Tooltip title="ダウンロード">
                      <IconButton
                        edge="end"
                        size="small"
                        component="a"
                        href={getDownloadUrl(file)}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <DownloadIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  }
                >
                  <ListItemIcon sx={{ minWidth: 32, mt: 0.5 }}>
                    {isImageFile(file.file_name || '') ? (
                      <ImageIcon fontSize="small" color="primary" />
                    ) : (
                      <FileIcon fontSize="small" color="action" />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="body2" noWrap sx={{ fontWeight: 500, maxWidth: 180 }}>
                        {file.file_name || '不明なファイル'}
                      </Typography>
                    }
                    secondary={
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.25, alignItems: 'center' }}>
                        <Typography variant="caption" color="text.secondary">
                          {file.sender_name}
                        </Typography>
                        <Typography variant="caption" color="text.disabled">·</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(file.created_at)}
                        </Typography>
                        {file.file_size && (
                          <>
                            <Typography variant="caption" color="text.disabled">·</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatFileSize(file.file_size)}
                            </Typography>
                          </>
                        )}
                        {file.storage_type === 'S3' && (
                          <CloudIcon sx={{ fontSize: 12, color: 'primary.main', ml: 0.25 }} />
                        )}
                      </Box>
                    }
                  />
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        )}
      </Box>

      {viewerMessageId && (
        <FileViewer
          open={!!viewerMessageId}
          onClose={() => setViewerMessageId(null)}
          messageId={viewerMessageId}
          fileName={viewerFileName}
        />
      )}
    </Box>
  );
};
